import React from 'react';
import { stringToColor } from '../../../utils/avatarUtils';
import { TableSkeleton } from '../../loading/skeletons';
import './ClientListView.css';

const IconView = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
  </svg>
);

const IconEdit = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
    <path d="M3 17.25V21h3.75l11.06-11.06-3.75-3.75L3 17.25zm2.92 2.33H5v-.92l9.06-9.06.92.92L5.92 19.58zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
  </svg>
);

const IconDelete = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
    <path d="M6 19c0 1.1.9 2 2 2h8a2 2 0 0 0 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
  </svg>
);

const IconRestore = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
    <path d="M13 3a9 9 0 0 0-9 9H1l4 4 4-4H6a7 7 0 1 1 7 7 1 1 0 1 0 0 2 9 9 0 1 0 0-18z" />
  </svg>
);

const IconEmail = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
    <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z" />
  </svg>
);

const IconPhone = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
    <path d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.02-.24c1.12.37 2.33.57 3.57.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1C10.4 21 3 13.6 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.24.2 2.45.57 3.57a1 1 0 0 1-.25 1.02l-2.2 2.2z" />
  </svg>
);

const sortIndicator = (active, direction) => {
  if (!active) return '↕';
  return direction === 'asc' ? '↑' : '↓';
};

const ClientListView = ({
  clients,
  isLoading = false,
  onViewProfile,
  onEdit,
  onDelete,
  onRestoreClient,
  listMode = 'active',
  sortBy,
  setSortBy,
  sortDirection,
  setSortDirection,
  selectedClients = [],
  onSelectClient,
  onSelectAll,
  canBulkEdit = false,
  formatDate = (date) => new Date(date).toLocaleDateString()
}) => {
  const isArchivedView = listMode === 'archived';
  const dateColumnLabel = isArchivedView ? 'Archived Date' : 'Added Date';
  const dateSortKey = isArchivedView ? 'archivedAt' : 'createdAt';
  const colCount = canBulkEdit ? 5 : 4;

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  const handleSort = (property) => {
    const isAsc = sortBy === property && sortDirection === 'asc';
    setSortDirection(isAsc ? 'desc' : 'asc');
    setSortBy(property);
  };

  const setSelectAllRef = (el) => {
    if (el) {
      el.indeterminate = selectedClients.length > 0 && selectedClients.length < clients.length;
    }
  };

  if (isLoading) {
    return (
      <div className="app-table-wrap client-list-view">
        <TableSkeleton
          rows={8}
          columns={[200, 180, 140, 100]}
          label="Loading clients"
        />
      </div>
    );
  }

  return (
    <div className="app-table-wrap client-list-view">
      <div className="client-list-view__scroll">
        <table className="app-data-table client-list-view__table" aria-label="Clients table">
          <thead>
            <tr>
              {canBulkEdit ? (
                <th className="client-list-view__checkHead">
                  <input
                    ref={setSelectAllRef}
                    type="checkbox"
                    checked={clients.length > 0 && selectedClients.length === clients.length}
                    onChange={onSelectAll}
                    aria-label="Select all clients"
                  />
                </th>
              ) : null}
              <th>
                <button type="button" className="app-table-sort" onClick={() => handleSort('name')}>
                  Client <span aria-hidden="true">{sortIndicator(sortBy === 'name', sortDirection)}</span>
                </button>
              </th>
              <th>Contact</th>
              <th>
                <button type="button" className="app-table-sort" onClick={() => handleSort(dateSortKey)}>
                  {dateColumnLabel}{' '}
                  <span aria-hidden="true">{sortIndicator(sortBy === dateSortKey, sortDirection)}</span>
                </button>
              </th>
              <th className="app-table-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {clients.length === 0 ? (
              <tr className="app-data-table-empty-row">
                <td colSpan={colCount}>No clients found.</td>
              </tr>
            ) : (
              clients.map((client) => {
                const displayName = `${client.firstName || ''} ${client.lastName || ''}`.trim() || 'Client';
                return (
                  <tr key={client.id} className="is-clickable" onClick={() => onViewProfile(client)}>
                    {canBulkEdit ? (
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedClients.includes(client.id)}
                          onChange={() => onSelectClient(client.id)}
                          aria-label={`Select ${displayName}`}
                        />
                      </td>
                    ) : null}
                    <td>
                      <div className="client-list-view__clientCell">
                        <div
                          className="client-list-view__avatar"
                          style={client.avatarUrl ? undefined : { backgroundColor: stringToColor(displayName) }}
                          aria-hidden="true"
                        >
                          {client.avatarUrl ? (
                            <img src={client.avatarUrl} alt="" />
                          ) : (
                            getInitials(client.firstName, client.lastName)
                          )}
                        </div>
                        <div className="client-list-view__identity">
                          <div className="client-list-view__name">{displayName}</div>
                          <div className="client-list-view__email">{client.email || '—'}</div>
                        </div>
                      </div>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="client-list-view__contact">
                        {client.email ? (
                          <a
                            className="app-icon-btn"
                            href={`mailto:${client.email}`}
                            title="Send email"
                            aria-label={`Email ${displayName}`}
                          >
                            <IconEmail />
                          </a>
                        ) : null}
                        {client.phone ? (
                          <a
                            className="app-icon-btn"
                            href={`tel:${client.phone}`}
                            title={`Call: ${client.phone}`}
                            aria-label={`Call ${displayName}`}
                          >
                            <IconPhone />
                          </a>
                        ) : null}
                        {!client.email && !client.phone ? (
                          <span className="client-list-view__muted">—</span>
                        ) : null}
                      </div>
                    </td>
                    <td>
                      {isArchivedView
                        ? (client.archivedAt ? formatDate(client.archivedAt) : 'Unknown')
                        : (client.createdAt ? formatDate(client.createdAt) : 'Recently added')}
                    </td>
                    <td className="app-table-actions" onClick={(e) => e.stopPropagation()}>
                      <div className="client-list-view__actions">
                        <button
                          type="button"
                          className="app-icon-btn"
                          onClick={() => onViewProfile(client)}
                          title="View profile"
                          aria-label={`View ${displayName}`}
                        >
                          <IconView />
                        </button>
                        {isArchivedView ? (
                          <button
                            type="button"
                            className="app-icon-btn app-icon-btn--success"
                            onClick={() => onRestoreClient?.(client)}
                            title="Restore client"
                            aria-label={`Restore ${displayName}`}
                          >
                            <IconRestore />
                          </button>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="app-icon-btn"
                              onClick={() => onEdit(client)}
                              title="Edit client"
                              aria-label={`Edit ${displayName}`}
                            >
                              <IconEdit />
                            </button>
                            <button
                              type="button"
                              className="app-icon-btn app-icon-btn--danger"
                              onClick={() => onDelete(client)}
                              title="Delete client"
                              aria-label={`Delete ${displayName}`}
                            >
                              <IconDelete />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ClientListView;
